import { motion, useReducedMotion } from "framer-motion";
import { Star, ArrowRight, Play } from "lucide-react";
import { LineReveal } from "@/lib/motion";
import { BUSINESS, waLink } from "@/data/site";

const badges = ["Professional Agency", "Fast Delivery", "100% Responsive", "SEO Ready", "AI Powered"];

export const Hero = () => {
  const reduce = useReducedMotion();
  return (
    <section className="relative min-h-[92svh] flex items-center overflow-hidden">
      <div className="absolute inset-0 radial-red -z-10" aria-hidden="true" />
      <div className="absolute inset-0 -z-10 opacity-[0.5]" aria-hidden="true"
        style={{ backgroundImage: "linear-gradient(hsl(240 6% 92%) 1px,transparent 1px),linear-gradient(90deg,hsl(240 6% 92%) 1px,transparent 1px)", backgroundSize: "56px 56px", maskImage: "radial-gradient(70% 70% at 50% 30%, #000 40%, transparent 100%)" }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-1.5 text-sm shadow-sm"
        >
          <span className="flex items-center gap-1 text-primary font-semibold">
            <Star className="w-4 h-4 fill-primary" /> {BUSINESS.rating}/5
          </span>
          <span className="text-muted-foreground">from {BUSINESS.reviews} founders</span>
        </motion.div>

        <h1 className="mt-6 text-5xl sm:text-6xl md:text-8xl font-heading font-extrabold tracking-tighter leading-[0.92] max-w-5xl">
          <LineReveal lines={["AI-Powered Websites", "That Help Businesses"]} delay={0.15} />
          <span className="block overflow-hidden">
            <motion.span
              className="block text-primary"
              initial={reduce ? { opacity: 0 } : { y: "110%" }}
              animate={reduce ? { opacity: 1 } : { y: "0%" }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              Grow.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.6 }}
          className="mt-7 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed"
        >
          We build premium websites, landing pages, SEO systems, AI automations and digital growth solutions for businesses across {BUSINESS.serviceArea}.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.6 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a href={waLink(`Hi ${BUSINESS.name}, I'd like a free website mockup.`)} target="_blank" rel="noopener noreferrer"
            data-testid="hero-primary-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-primary hover:bg-[#c81f1f] text-primary-foreground font-semibold px-7 py-4 transition-colors">
            Get Free Website Mockup
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </a>
          <a href={waLink(`Hi ${BUSINESS.name}, I'd like to book a free consultation.`)} target="_blank" rel="noopener noreferrer"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-white hover:border-foreground/30 font-semibold px-7 py-4 transition-colors">
            <Play className="w-4 h-4 text-primary" /> Book Free Consultation
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-14 flex flex-wrap gap-x-6 gap-y-3 text-sm text-muted-foreground"
        >
          {badges.map((b) => (
            <span key={b} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" /> {b}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
